import React from 'react';

import { makeId, plural } from '@reckon-web/utils';
import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { LoadingDots } from '@reckon-web/loading';
import { Button } from '@reckon-web/button';
import { PlusIcon } from '@reckon-web/icon';
import { SpotAttention, SpotNoResults } from '@reckon-web/illustration';
import { Heading } from '@reckon-web/heading';
import { Divider } from '@reckon-web/divider';
import { TextLink } from '@reckon-web/text-link';

import { dateCompare, maybeify, stringCompare } from '../../../utils/sort';
import {
  JobWithTasks,
  useQueryJobsWithTasks,
} from '../../../hooks/use-query-jobs-with-tasks';
import { CountByStatusBadges } from '../../common/status-badge';
import { KanbanOrList } from '../kanban-or-list';
import { SortType } from '../sort';
import { Opts } from '../../job/new-job-dialog';
import { ErrorScreen } from '../../common/error-screen';
import { Loader } from '../../common/loader';
import { useGetJobStatusList } from '../../../data/useGetJobStatusList';

import { KanbanView } from './kanban-view';
import { ListView } from './list-view';

const NoJobs: React.FC<{ onClickCreateJob?: () => void; testId?: string }> = ({
  onClickCreateJob,
  testId = `no-jobs`,
}) => (
  <Flex
    direction="column"
    alignItems="center"
    justifyContent="center"
    gap="large"
    height="100%"
    background="muted"
    borderRadius="medium"
    marginBottom="xxlarge"
    paddingBottom="xxlarge"
    data={{ testid: testId }}
  >
    <SpotAttention />
    <Heading level="3">You don't have any jobs yet</Heading>
    {onClickCreateJob && (
      <Button
        label="New job"
        iconBefore={PlusIcon}
        onClick={() => onClickCreateJob()}
        data-testid={makeId(testId, 'new-job')}
      />
    )}
  </Flex>
);

const NoJobsFound: React.FC<{ clearFilters?: () => void; testId?: string }> = ({
  clearFilters,
  testId = `no-jobs-found`,
}) => (
  <Flex
    direction="column"
    alignItems="center"
    justifyContent="center"
    gap="large"
    height="100%"
    background="muted"
    borderRadius="medium"
    marginBottom="xxlarge"
    paddingBottom="xxlarge"
    data={{ testid: testId }}
  >
    <SpotNoResults />
    <Heading level="3">No jobs found</Heading>
    <Text>
      There are no jobs matching your queries. Please refine your filters.
    </Text>
    <Button
      weight="subtle"
      tone="active"
      label="Clear filters"
      onClick={clearFilters}
      data-testid={makeId(testId, 'clear-filters')}
    />
  </Flex>
);

// strikes me that this, and the const compare = React.useCallback bit, could be in some kind of useGetJobKanbanCompare hook
const compareByDueAsc = (a: JobWithTasks, b: JobWithTasks) =>
  maybeify(dateCompare)(a.dueDate, b.dueDate);
const compareByDueDesc = (a: JobWithTasks, b: JobWithTasks) =>
  maybeify(dateCompare, false)(a.dueDate, b.dueDate);
const compareByNameAsc = (a: JobWithTasks, b: JobWithTasks) =>
  maybeify(stringCompare)(a.name, b.name);
const compareByNameDesc = (a: JobWithTasks, b: JobWithTasks) =>
  maybeify(stringCompare, false)(a.name, b.name);
const comparersBySortType: Record<
  SortType,
  ((a: JobWithTasks, b: JobWithTasks) => number)[]
> = {
  'Due Date (earliest first)': [compareByDueAsc, compareByNameAsc],
  'Due Date (latest first)': [compareByDueDesc, compareByNameDesc],
  'Name (A → Z)': [compareByNameAsc, compareByDueAsc],
  'Name (Z → A)': [compareByNameDesc, compareByDueDesc],
};

export const Jobs: React.FC<{
  kanbanOrList: KanbanOrList;
  queryResult: ReturnType<typeof useQueryJobsWithTasks>;
  filtered: boolean | string;
  clearFilter: () => void;
  sort: SortType;
  onClickJob?: (jobId: string) => void;
  onClickCreateJob?: (opts?: Opts) => void;
  testId?: string;
}> = ({
  kanbanOrList,
  queryResult,
  filtered: _filtered,
  clearFilter,
  sort,
  onClickJob,
  onClickCreateJob,
  testId = `jobs`,
}) => {
  // useCallback so it's stable
  const compare = React.useCallback(
    (a: JobWithTasks, b: JobWithTasks) => {
      const comparers = comparersBySortType[sort];
      for (const comparer of comparers) {
        const result = comparer(a, b);
        if (result !== 0) {
          return result;
        }
      }
      // got all the way to here, they must be equal
      return 0;
    },
    [sort]
  );

  const { loading, error, data } = queryResult;
  const [state, setState] = React.useState({
    data,
    filtered: _filtered,
  });
  React.useEffect(
    () => data && setState({ data, filtered: _filtered }),
    [data, _filtered]
  );

  // even though we don't use the statuses here, I'd like them to participate in the showing of the BIG loader
  // so we do the load here
  const {
    // loading: loadingStatuses,
    error: errorStatuses,
    data: dataStatuses,
  } = useGetJobStatusList();

  if (error || errorStatuses) return <ErrorScreen />;

  const { data: jobs, filtered } = state;

  return (
    <Flex direction="column" height="100%" data={{ testid: testId }}>
      <Flex alignItems="center" gap="small" paddingY="medium" flexShrink={0}>
        {jobs && (
          <Text
            size="xsmall"
            color="muted"
            data={{ testid: makeId(testId, 'showing') }}
          >
            Showing {plural(jobs.length, 'job', 'jobs')}
            {filtered && (
              <Text
                inline
                size="xsmall"
                color="dim"
                weight="medium"
                data={{ testid: makeId(testId, 'filtered') }}
              >
                &nbsp;
                {typeof filtered === 'string'
                  ? `(filtered - ${filtered})`
                  : '(filtered)'}
              </Text>
            )}
          </Text>
        )}
        {jobs && loading && (
          <LoadingDots
            label="loading"
            size="small"
            data-testid={makeId(testId, 'loading-dots')}
          />
        )}
        <Flex flexGrow={1} />
        {kanbanOrList === 'list' && jobs && (
          <CountByStatusBadges
            jobs={jobs}
            testId={makeId(testId, 'status-badge')}
          />
        )}
        <Text size="small" weight="medium">
          <TextLink
            href="/closed-jobs"
            color="base"
            data={{ testid: makeId(testId, 'closed-jobs-link') }}
          >
            View closed jobs
          </TextLink>
        </Text>
        {onClickCreateJob && (
          <Button
            label="New job"
            iconBefore={PlusIcon}
            size="small"
            weight="subtle"
            onClick={() => onClickCreateJob()}
            data-testid={makeId(testId, 'new-job')}
          />
        )}
      </Flex>
      {(() => {
        if (!jobs || !dataStatuses)
          return (
            <Flex direction="column" marginBottom="large" flexGrow={1}>
              <Loader
                size="large"
                label="Loading jobs"
                data-testid={makeId(testId, 'loader')}
              />
            </Flex>
          );

        if (jobs.length > 0) {
          return (
            <React.Fragment>
              {kanbanOrList === 'list' && <Divider />}
              <Flex
                direction="column"
                flexGrow={1}
                marginBottom="large"
                overflow="auto"
              >
                {kanbanOrList === 'kanban' && (
                  <KanbanView
                    jobs={jobs}
                    jobStatuses={dataStatuses}
                    compare={compare}
                    onClickJob={onClickJob}
                    testId={makeId(testId, 'kanban-view')}
                  />
                )}
                {kanbanOrList === 'list' && (
                  <ListView jobs={jobs} testId={makeId(testId, 'list-view')} />
                )}
              </Flex>
            </React.Fragment>
          );
        }
        if (filtered) {
          return (
            <NoJobsFound
              clearFilters={clearFilter}
              testId={makeId(testId, 'no-jobs-found')}
            />
          );
        }
        return (
          <NoJobs
            onClickCreateJob={onClickCreateJob}
            testId={makeId(testId, 'no-jobs')}
          />
        );
      })()}
    </Flex>
  );
};
